import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import _ from 'lodash';
import cx from 'classnames';

import styles from './collection.module.scss';
import { addAssetToCollection, createSection, editCollection, fetchCollection, fetchCollectionSection, fetchCollectionSharedContacts, invalidateCollectionUpdateState, invalidateEditCollection, moveAsset, moveAssetToSection, moveSection, removeAssetFromCollection, removeSection, renameSection, updateAssetMetadata, updateCollection, updateCollectionProperty } from "../../redux/actions/collections";
import { useSelector } from "react-redux";
import { BENTO_EXPERIENCE, CAROUSEL_EXPERIENCE, METRO_EXPERIENCE } from "../../constants/collections";
import { BentoExperience } from "./experiences/bento";
import { CarouselExperience } from "./experiences/carousel";
import { MetroExperience } from "./experiences/metro";
import { Menubar, MenuState } from "./components/menu";
import { Collection, CollectionAsset, CollectionChangeRequest } from "../../types/collections";
import { calculateUpdateRequest, shouldNotifyOnChange } from "../../utils/collections.helper";
import Reveal from "src/components/animation";
import { AddAssetsDialog } from "src/components/asset/add";
import { CollectionContext } from "./components/context";
import { NotifyChangesDialog } from "./components/live";
import { generateLink, invalidateAllLinks } from "src/redux/actions/link";
import buildUrl from "build-url-ts";
import { startBackgroundUploadTask } from "src/redux/actions/backgroundTask";
import { Asset, AssetUpload } from "src/types/assets";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import EmptyMessage from "src/components/emptyMessage";
import { toggleAssetStatusInContentSimilarityQueue } from "src/redux/actions/assets";

const emptyMessageIcon = '/images/Unauthorized_Asset.svg';

const CollectionPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, sectionId } = useParams();

  const [menuState, setMenuState] = React.useState<MenuState>({
    primaryAction: 'view'
  });

  const [notifyRecipients, setNotifyRecipients] = useState<boolean>(false);

  const user = useSelector((state: any) => _.get(state, 'users.me'));
  const loading = useSelector((state: any) => _.get(state, `collections.collection['${id}'].loading`, false));
  const loaded = useSelector((state: any) => _.get(state, `collections.collection['${id}'].loaded`, false));

  const persistedCollection = useSelector((state: any) => {
    return _.get(state, `collections.collection['${id}'].item`);
  });

  const collection = useSelector((state: any) => {
    const editCollection = _.get(state, `collections.edit`);
    if (editCollection) {
      return editCollection;
    }

    return _.get(state, `collections.collection['${id}'].item`);
  });

  const updated = useSelector((state: any) => _.get(state, `collections.update['${id}'].updated`, false));
  const errorMessage = useSelector((state: any) => _.get(state, `collections.collection.[${id}].error.errors`, false));

  const socialLinkGenerated = useSelector((state: any) => {
    const creating = _.get(state, 'link.generateLink.creating');
    if (creating) {
      return null;
    }

    return _.get(state, 'link.generateLink.link');
  });

  const experienceType = _.get(collection, 'viewerType');
  const editing = menuState.primaryAction !== 'view';

  const paperfliteEmailDisabled = useSelector((state: any) => {
    const paperfliteEmailServerEnabled = _.get(state, 'account.item.paperfliteEmailServerEnabled', true);
    return !paperfliteEmailServerEnabled;
  });

  const { 
    trackCollectionViewed, 
    trackSectionSelected,  
    trackSectionAdded,
    trackAddAssetClicked,
    trackCollectionShared,
  } = useCollectionsAdoption();

  useEffect(() => {
    if (id) {
      dispatch(fetchCollection(id));
    }

    return () => {
      dispatch(invalidateEditCollection(id!));
      dispatch(invalidateCollectionUpdateState());
    }
  }, [id]);

  useEffect(() => {
    if (loaded) {

      // Track Collection Viewed
      trackCollectionViewed(collection);

      const sections = _.size(_.get(persistedCollection, 'sections', []));
      if (sections == 0) {
        if (sectionId) {
          navigate(`/collections/${id}`, {
            replace: true
          });
        }

        return;
      }

      let sectionIndex = 0;
      if (sectionId && sections > 0) {
        sectionIndex = parseInt(sectionId);
        if (sectionIndex < 0 || sectionIndex >= sections) {
          sectionIndex = 0;
        }
      }

      navigate(`/collections/${id}/sections/${sectionIndex}`, {
        replace: true
      });
    }
  }, [loaded]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (menuState.primaryAction === 'edit') {
      dispatch(editCollection(collection));
    } else if (menuState.primaryAction === 'view') {
      dispatch(invalidateEditCollection(id!));

      const activeSection = parseInt(sectionId || '0');
      const activeIndex = validateSectionIndex(activeSection, _.size(_.get(persistedCollection, 'sections', [])));

      if (!_.isEqual(sectionId, activeIndex)) {
        if (!activeIndex) {
          navigate(`/collections/${id}`, {
            replace: true
          });
        } else {
          navigate(`/collections/${id}/sections/${activeIndex}`, {
            replace: true
          });
        }
      }
    }
  }, [loaded, menuState.primaryAction]);

  useEffect(() => {
    if (updated && menuState.primaryAction === 'edit') {
      setMenuState({ primaryAction: 'view' });
      dispatch(invalidateEditCollection(id!));
      dispatch(invalidateCollectionUpdateState());
    }
  }, [menuState.primaryAction, updated]);

  useEffect(() => {
    switch (menuState.secondaryAction) {
      case 'save': {
        handleSave();
        setMenuState(state => ({ ...state, secondaryAction: null }));
        break;
      }
      case 'reset': {
        if (menuState.primaryAction === 'edit') {
          setMenuState(state => ({
            ...state,
            primaryAction: 'view',
            secondaryAction: null
          }));
        }

        break;
      }
      case 'social-share': {
        if (menuState.primaryAction === 'view') {
          const data = menuState.data;
          handleSocialShare(data);
        }

        break;
      }
      case 'add-asset': {
        trackAddAssetClicked(collection);
        break;
      }
      default:
        break;
    }
  }, [menuState.secondaryAction, menuState.data]);

  useEffect(() => {
    if (socialLinkGenerated) {
      if (menuState.primaryAction === 'view' && menuState.secondaryAction == 'social-share') {
        const data = menuState.data;
        handleSocialLinkGenerated(socialLinkGenerated, data);
      }

      dispatch(invalidateAllLinks());
      setMenuState(state => ({
        ...state,
        primaryAction: 'view',
        secondaryAction: null,
        data: null
      }));
    }
  }, [socialLinkGenerated]);

  useEffect(() => {
    // Track Section Selected Event
    if(loaded){
      const sectionItem = _.get(collection, `sections[${sectionId}].item`);

      if(sectionItem){
        trackSectionSelected(collection, sectionItem);
      }
    }
  }, [loaded, sectionId]);

  const validateSectionIndex = (index: number, sections: number) => {
    if (sections == 0) {
      return undefined;
    } else if (index < 0 || index >= sections) {
      return "0";
    } else {
      return "" + index;
    }
  }

  const handleMenuStateChange = (value) => {
    setMenuState(value);
  }

  // Section Actions
  const handleFetchSection = (section: string) => {
    dispatch(fetchCollectionSection({
      collection: id!,
      section: section
    }));
  }

  const handleSectionClick = (section: any) => {
    navigate(`/collections/${id}/sections/${section}`);
  }

  const handleAddSection = (id: string, name: string) => {
    dispatch(createSection({
      collection: id,
      name: name
    }));

    trackSectionAdded({
      collection: collection,
      section: name
    });
  }

  const handleRemoveSection = (section: number) => {

    if(_.size(collection.sections) === 1){
      const section = _.get(collection, "sections[0]", {});
      const assets = _.get(section, "item.entities", []);
      dispatch(addAssetToCollection({
        collection: collection.id,
        assets: assets.map(item => ({ id: item.id, asset: item.asset } as CollectionAsset)),
        addAt: "end"
      }));
    }
    dispatch(removeSection({
      collection: collection.id!,
      index: section,
    }));
  }

  const handleRenameSection = (section: number, name: string) => {
    dispatch(renameSection({
      collection: collection.id!,
      index: section,
      name: name
    }));
  }

  const handleMoveSection = (from: number, to: number) => {
    dispatch(moveSection({
      collection: collection.id!,
      from: from,
      to: to
    }));
  }

  const handleRemoveAsset = (section: number | null, asset: string) => {
    dispatch(removeAssetFromCollection({
      collection: collection.id!,
      section: section ?? undefined,
      asset: asset
    }));
  }

  const handleAssetMove = (section: number, from: number, to: number) => {
    dispatch(moveAsset({
      collection: id!,
      section: section,
      from: from,
      to: to
    }));
  }

  const handleAssetMoveToSection = (source: number, target: number, asset: CollectionAsset) => {
    dispatch(moveAssetToSection({
      collection: collection.id!,
      source: source,
      target: target,
      asset: asset
    }));
  }

  const handleUpdateAssetMetadata = (section: number | null, updates: { id: string; metadata: any; }[]) => {
    dispatch(updateAssetMetadata({
      collection: id!,
      section: section ?? undefined,
      updates: updates
    }));
  }

  const handleUpdateCollectionProperty = (property: string, value: any) => {
    dispatch(updateCollectionProperty({
      collection: collection.id!,
      property: property,
      value: value
    }));
  }

  const handleSave = () => {
    const shouldNotify = shouldNotifyOnChange(persistedCollection, collection);
    if (collection.live && collection.sharedRecipientsCount > 0 && shouldNotify && !paperfliteEmailDisabled) {
      setNotifyRecipients(true);
    } else {
      handleSaveConfirm(false, '', '');
    }
  }

  const handleNotifyRecipientConfirmation = (notifyRecipients, subject, message) => {
    handleSaveConfirm(notifyRecipients, subject, message);
  }

  const handleSaveConfirm = (notifyRecipients, subject, message) => {
    const changes: CollectionChangeRequest = calculateUpdateRequest(persistedCollection, collection);

    changes.notifyRecipients = notifyRecipients;
    changes.subject = subject;
    changes.message = message;

    dispatch(updateCollection({
      id: collection.id,
      request: changes
    }));
  }

  const handleBack = () => {
    navigate('/collections');
  }

  const handleCloseAddAsset = () => {
    setMenuState({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleAddAssetFinish = (entities: (Asset | AssetUpload |  Collection)[]) => {
    if (_.size(entities) > 0) {

      const uploading = entities.filter(item => item.entityType == 'asset-upload');
      if (!_.isEmpty(uploading)) {
        const assest = entities.filter(item => _.includes(['asset', 'asset-upload'], item.entityType));
        dispatch(startBackgroundUploadTask({
          assets: assest as [Asset | AssetUpload],
          entity: {
            id: collection.id!,
            entityType: 'collection',
            name: collection.name,
            url: `/collections/${collection.id}`,
            section: sectionId
          }
        }));
      } else {
        const assets = entities.filter(item => item.entityType == 'asset');
        dispatch(addAssetToCollection({
          collection: collection.id,
          section: sectionId ? parseInt(sectionId) : undefined,
          assets: assets.map(item => ({ id: item.id, asset: item } as CollectionAsset)),
          addAt: "end"
        }));
        
        _.map(assets, (item) => {
          if (item.entityType === 'asset' && _.get(item,"upload", false)) {
            dispatch(toggleAssetStatusInContentSimilarityQueue({ asset: item as Asset, initialise: true }));
          }
        });
      }
    }

    handleCloseAddAsset();
  }

  const handleGetRecipient = (recipient) => {
    return recipient
  }

  const handleFetchRecipientsForPage = (page) => {
    dispatch(fetchCollectionSharedContacts({
      collection: id!,
      page: page,
      limit: 10,
    }));
  }

  const getNotificationContentForConversation = () => {
    return {
      subject: `${collection?.name || ""} was updated`,
      message: `<span>Hello there,</span><br/><br/>We've recorded some changes to the content collection
      ${collection?.name || ""} that was shared with you by <a href='mailto:${user.email}'>${user.email}</a>.<br/><br/>`
    }
  }

  const handleSocialShare = (type) => {
    dispatch(generateLink({
      entityID: collection.id!,
      entityType: 'collection',
      type: 'anonymous',
      utmCampaign: {
        source: type
      }
    }))
  }

  const handleSocialLinkGenerated = (link, type) => {
    let url = link.url;

    const queryParams = {
      _: new Date().getTime()
    };

    const utmCampaign = _.omitBy(link.utmCampaign, _.isNil);
    _.forOwn(utmCampaign, (value, key) => {
      queryParams[`utm_${key}`] = value
    });

    url = buildUrl(url, {
      queryParams: queryParams
    });

    url = encodeURI(url);

    const winTop = (window.screen.height - 500) / 2;
    const winLeft = (window.screen.width - 575) / 1.75;
    const collectionName = _.get(collection, 'name', '');

    let shareUrl: string | null = null;
    switch (type) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer.php?u=${url}&title=${collectionName}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${collectionName}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/share?url=${url}&title=${collectionName}`;
        break;
    }

    trackCollectionShared(collection);

    if (shareUrl) {
      window.open(shareUrl, 'sharer', 'top=' + winTop + ',left=' + winLeft + ',toolbar=0,status=0,width=' + 500 + ',height=' + 575);
    }
  }

  useEffect(() => {
    setCollectionContextState(calculateCollectionContextState());
  }, [collection]);

  const calculateCollectionContextState = () => {
    return {
      id: id,
      entityType: 'collection',
      itemSelector: `collections.collection['${id}'].item`,
      editSelector: `collections.edit`,
      updateSelector: `collections.update['${id}']`,
      recipientSelector: `collections.collection['${id}'].recipients`,
      fetchSection: handleFetchSection,
      onSectionClick: handleSectionClick,
      createSection: handleAddSection,
      removeSection: handleRemoveSection,
      renameSection: handleRenameSection,
      moveSection: handleMoveSection,

      removeAsset: handleRemoveAsset,
      moveAsset: handleAssetMove,
      moveAssetToSection: handleAssetMoveToSection,

      updateAssetMetadata: handleUpdateAssetMetadata,
      updateCollectionProperty: handleUpdateCollectionProperty,

      getRecipient: handleGetRecipient,
      fetchRecipientsForPage: handleFetchRecipientsForPage,

      getNotificationContent: getNotificationContentForConversation
    }
  }

  // Collection State
  const [collectionContextState, setCollectionContextState] = useState<any>(calculateCollectionContextState());

  if (loading && !collection) {
    return <div className={styles.loading}>Loading collection...</div>
  }

  const renderError = () => {
    if (errorMessage && errorMessage.length) {
      const error = errorMessage[0];

      if (error.code === 10002) {
        return (
          <div className={styles.emptyContainer}>
          <EmptyMessage
            width={450}
            heading="Sorry!  Some one passed you the wrong information. You were never supposed to see me :). The stream in me does not exist any longer."
            image={emptyMessageIcon}
          />
          </div>
        );
      } else if (error.code === 10007) {
        return (
          <div className={styles.emptyContainer}>
          <EmptyMessage    
            width={450}
            heading= "I’m sorry! It seems like I’m not authorised to show what you are looking for. I hate disappointing you :("
            image={emptyMessageIcon}
          />
          </div>
        );
      }
    }
    return null;
  };

  if (errorMessage) {
    return <div className={styles.errorContainer}>{renderError()}</div>;
  }

  if (!collection) {
    return <div>No Collection</div>
  }

  return (
    <CollectionContext.Provider value={collectionContextState}>
      <div className={styles.container}>
        <div className={cx(styles.menubar, {
          [styles.editing]: menuState.primaryAction !== 'view'
        })}>

          <Reveal className={styles.backButtonContainer} show={!editing}>
            <button className={styles.backButton} onClick={handleBack}>
              <i className="paperflite-arrow-up"></i>
            </button>
          </Reveal>

          <Menubar
            collection={collection}
            menuState={menuState}
            onChange={handleMenuStateChange}
          />

        </div>
        <div className={styles.experience}>
          {experienceType == BENTO_EXPERIENCE &&
            <BentoExperience
              collection={collection}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {experienceType == CAROUSEL_EXPERIENCE &&
            <CarouselExperience
              collection={collection}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {experienceType == METRO_EXPERIENCE &&
            <MetroExperience
              collection={collection}
              menuState={menuState}
              onMenuChange={handleMenuStateChange}
            />
          }

          {menuState.secondaryAction == 'add-asset' &&
            <AddAssetsDialog show={menuState.secondaryAction == 'add-asset'} 
              selectableEntityTypes={['asset']}
              source={{
                type: 'collection',
                entity: collection,
              }}
              onFinish={handleAddAssetFinish} 
              onClose={handleCloseAddAsset} 
            />
          }

          <NotifyChangesDialog
            show={notifyRecipients}
            collection={collection}
            onConfirm={handleNotifyRecipientConfirmation}
            onClose={() => setNotifyRecipients(false)}
          />
        </div>
      </div>
    </CollectionContext.Provider>
  );
}

export { CollectionPage };